import React from 'react';
import { Link } from 'react-router-dom';
import useLang from '../../i18n/useLang';
import { SectionHeading, Chips } from '../site/PageParts';
import { Reveal } from '../site/motion';
import mekorotLogo from '../../images/clients/Mekorot.webp';

// Card copy lives in the translation files; logos and links are not translated, so they are keyed by id here.
const LOGOS = { mekorot: mekorotLogo };
const LINKS = { campro: 'https://campro.co.il' };

function OutLink() {
  return (
    <svg className="h-4 w-4 shrink-0 rtl:-scale-x-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M7 17L17 7" />
      <path d="M8 7h9v9" />
    </svg>
  );
}

export default function RecentWork() {
  const { t } = useLang();
  const asList = (k) => {
    const v = t(k, { returnObjects: true });
    return Array.isArray(v) ? v : [];
  };
  const main = asList('home.work.items');
  const other = asList('home.work.other');
  if (!main.length && !other.length) return null;

  return (
    <section className="bg-mint-50 py-24 sm:py-32">
      <div className="wrap">
        <Reveal>
          <SectionHeading title={t('home.work.title')} lead={t('home.work.lead')} />
        </Reveal>

        <ul className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2">
          {main.map((item, i) => (
            <Reveal as="li" key={item.id} delay={i * 110} variant="scale" className="flex flex-col rounded-4xl bg-white p-8 shadow-card ring-1 ring-fog-200 sm:p-10">
              {LOGOS[item.id] && <img src={LOGOS[item.id]} alt="" className="mb-7 h-10 w-auto self-start object-contain" />}
              <h3 className="heading text-2xl text-ink sm:text-[1.75rem]">{item.title}</h3>
              <p className="mt-3 text-[1.05rem] leading-relaxed text-ink-600">{item.body}</p>
              <div className="mt-auto">
                <Chips items={item.stack} />
              </div>
            </Reveal>
          ))}
        </ul>

        {other.length > 0 && (
          <>
            <Reveal as="h3" className="mt-16 text-[0.95rem] font-medium text-ink-500">
              {t('home.work.otherTitle')}
            </Reveal>
            <ul className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              {other.map((item, i) => {
                const href = LINKS[item.id];
                const body = (
                  <>
                    <span className="flex items-center gap-2 text-xl font-semibold text-ink">
                      <span>{item.title}</span>
                      {href && <OutLink />}
                    </span>
                    <span className="mt-2 block text-[1rem] leading-relaxed text-ink-600">{item.body}</span>
                    <Chips items={item.stack} />
                  </>
                );
                const cls = 'block h-full rounded-4xl bg-white/70 p-7 ring-1 ring-mint-200';
                return (
                  <Reveal as="li" key={item.id} delay={i * 110}>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${cls} transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-card`}
                      >
                        {body}
                      </a>
                    ) : (
                      <div className={cls}>{body}</div>
                    )}
                  </Reveal>
                );
              })}
            </ul>
          </>
        )}

        <Reveal as="p" className="mt-12 text-lg text-ink-600">
          {t('home.work.before')}{' '}
          <Link to="/Customers" className="font-medium text-grovv-800 underline decoration-grovv/40 underline-offset-4 transition-colors hover:decoration-grovv-800">
            {t('home.work.beforeLink')}
          </Link>
        </Reveal>
      </div>
    </section>
  );
}
